import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  BulkUploadResponse,
  MissingDataError,
  DuplicateError,
  ConsolidatedRowError
} from '../../interfaces/stock.interface';

@Component({
  selector: 'app-bulk-upload-results-modal',
  templateUrl: './bulk-upload-results-modal.component.html',
  styleUrls: ['./bulk-upload-results-modal.component.scss']
})
export class BulkUploadResultsModalComponent {
    expandedSections = {
    missingData: false,
    duplicates: false,
    consolidations: false
  };

  readonly maxDisplayItems = 50;
  
  constructor(
    private dialogRef: MatDialogRef<BulkUploadResultsModalComponent>,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: { response: BulkUploadResponse }
  ) {}

  get response(): BulkUploadResponse {
    return this.data.response;
  }

  toggleSection(section: 'missingData' | 'duplicates' | 'consolidations'): void {
    this.expandedSections[section] = !this.expandedSections[section];
  }

  get hasMissingData(): boolean {
    return (this.response.validationErrors?.missingDataRows?.length ?? 0) > 0;
  }

  get hasDuplicates(): boolean {
    return (this.response.validationErrors?.duplicateRows?.length ?? 0) > 0;
  }

  get hasConsolidations(): boolean {
    return (this.response.validationErrors?.consolidatedRows?.length ?? 0) > 0;
  }

  get hasIssues(): boolean {
    return this.hasMissingData || this.hasDuplicates;
  }

  get hasPositiveInfo(): boolean {
    return this.hasConsolidations;
  }

  getSkippedDueToMissingData(): number {
    const value = this.response.summary?.skippedDueToMissingData || 0;
    if (value < 0) {
      return 0;
    }
    return value;
  }

  getSkippedDueToDuplicates(): number {
    const value = this.response.summary?.skippedDueToDuplicates || 0;
    if (value < 0) {
      return 0;
    }
    return value;
  }

  getTotalMissingDataCount(): number {
    return this.response.validationErrors?.missingDataRows?.length ?? 0;
  }

  getTruncatedMissingData(): MissingDataError[] {
    const data = this.response.validationErrors?.missingDataRows ?? [];
    return data.slice(0, this.maxDisplayItems);
  }

  isMissingDataTruncated(): boolean {
    return this.getTotalMissingDataCount() > this.maxDisplayItems;
  }

  getTotalDuplicatesCount(): number {
    return this.response.validationErrors?.duplicateRows?.length ?? 0;
  }

  getTruncatedDuplicates(): DuplicateError[] {
    const data = this.response.validationErrors?.duplicateRows ?? [];
    return data.slice(0, this.maxDisplayItems);
  }

  isDuplicatesTruncated(): boolean {
    return this.getTotalDuplicatesCount() > this.maxDisplayItems;
  }

  getTotalConsolidationsCount(): number {
    return this.response.validationErrors?.consolidatedRows?.length ?? 0;
  }

  getTruncatedConsolidatedRows(): ConsolidatedRowError[] {
    const data = this.response.validationErrors?.consolidatedRows ?? [];
    return data.slice(0, this.maxDisplayItems);
  }

  isConsolidatedRowsTruncated(): boolean {
    return this.getTotalConsolidationsCount() > this.maxDisplayItems;
  }

  getConsolidatedRowsCount(): number {
    return this.response.validationErrors?.consolidatedRows?.length ?? 0;
  }

  getMissingFieldsText(missingFields: string[]): string {
    return missingFields.join(', ');
  }

  exportResults(): void {
    const results = this.generateExportData();
    const blob = new Blob([results], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `bulk-upload-results-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private generateExportData(): string {
    const headers = ['Tipo', 'Fila', 'Codigo', 'Descripcion', 'Campos Faltantes', 'Mensaje'];
    const rows = [headers.join(',')];

    if (this.response.validationErrors?.missingDataRows) {
      this.response.validationErrors.missingDataRows.forEach(error => {
        const row = [
          'Datos Faltantes',
          error.rowNumber.toString(),
          '',
          `"${error.productDescription}"`,
          `"${error.missingFields.join(', ')}"`,
          'Fila omitida por datos faltantes'
        ];
        rows.push(row.join(','));
      });
    }

    if (this.response.validationErrors?.duplicateRows) {
      this.response.validationErrors.duplicateRows.forEach(error => {
        const row = [
          'Duplicado',
          error.rowNumber.toString(),
          error.code,
          `"${error.productDescription}"`,
          '',
          `"${error.message}"`
        ];
        rows.push(row.join(','));
      });
    }

    if (this.response.validationErrors?.consolidatedRows) {
      this.response.validationErrors.consolidatedRows.forEach(consolidation => {
        const row = [
          'Consolidado',
          consolidation.rowNumbers.join(';'),
          consolidation.code,
          `"${consolidation.productDescription}"`,
          '',
          `"${consolidation.message}"`
        ];
        rows.push(row.join(','));
      });
    }

    return rows.join('\n');
  }

  close(): void {
    this.dialogRef.close();
  }

  getFieldValue(fieldValues: { [key: string]: string }, fieldName: string): string {
    if (fieldValues[fieldName]) {
      return fieldValues[fieldName];
    }

    const baseName = fieldName.split('(')[0].trim();
    if (fieldValues[baseName]) {
      return fieldValues[baseName];
    }

    for (const key in fieldValues) {
      if (key.toLowerCase().includes(baseName.toLowerCase())) {
        return fieldValues[key];
      }
    }
    
    return this.translate.instant('stock.bulkUpload.results.emptyValue');
  }
}
